import type * as lazyIndex from './lazyIndex';
import {
    createLazyComponent,
    registerLazyOrchestrator,
    LazyImport,
    LazyModule,
    type LazyComponent,
} from 'owa-bundling';
import { endSearchSession } from 'owa-search-actions';
import { onAppBarTrigger } from 'owa-nova-actions';
import type { ModifiedQueryInformationalViewProps } from './components/ModifiedQueryInformationalView';
import type { SearchScopePickerProps } from './components/SearchScopePicker';
import type { ISearchButtonProps } from './components/CompactSearchBox';
import type { SearchScopePickerV9Props } from './components/SearchScopePickerV9';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(
    () => import(/* webpackChunkName: "Lightable"*/ './lazyIndex'),
    {
        name: 'Lightable',
    }
);

export const lazySetLatestQFRequestId: LazyImport<
    typeof lazyIndex.setLatestQFRequestId,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.setLatestQFRequestId);

// Export mutators
export const SearchBoxDropdown: LazyComponent<lazyIndex.SearchBoxDropdownProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.SearchBoxDropdown);
export const SearchBoxDropdownV9: LazyComponent<lazyIndex.SearchBoxDropdownV9Props, unknown, void> =
    createLazyComponent(lazyModule, m => m.SearchBoxDropdownV9);
export const SearchBoxPillWell: LazyComponent<lazyIndex.SearchBoxPillWellProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.SearchBoxPillWell);
export const SuggestionsCallout: LazyComponent<lazyIndex.SuggestionsCalloutProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.SuggestionsCallout);
export const CompactSearchBox: LazyComponent<ISearchButtonProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.CompactSearchBox);
export const SearchScopePicker: LazyComponent<SearchScopePickerProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.SearchScopePicker);
export const ModifiedQueryInformationalView: LazyComponent<
    ModifiedQueryInformationalViewProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.ModifiedQueryInformationalView);
export const SearchScopePickerV9: LazyComponent<SearchScopePickerV9Props, unknown, void> =
    createLazyComponent(lazyModule, m => m.SearchScopePickerV9);

// Export selectors
export const lazyGetSuggestionAtIndex: LazyImport<
    typeof lazyIndex.getSuggestionAtIndex,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getSuggestionAtIndex);
export const lazyIsInSearchMode: LazyImport<typeof lazyIndex.isInSearchMode, typeof lazyModule> =
    new LazyImport(lazyModule, m => m.isInSearchMode);
export const lazyIsQFRequestIdEqualToLatest: LazyImport<
    typeof lazyIndex.isQFRequestIdEqualToLatest,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.isQFRequestIdEqualToLatest);
export const lazyIsSearchBoxEmpty: LazyImport<
    typeof lazyIndex.isSearchBoxEmpty,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.isSearchBoxEmpty);
export const lazyHasSuggestionPills: LazyImport<
    typeof lazyIndex.hasSuggestionPills,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.hasSuggestionPills);
export const lazyGetCopilotChatQuery: LazyImport<
    typeof lazyIndex.getCopilotChatQuery,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getCopilotChatQuery);

export const lazyWarmupCopilotSearchSydney: LazyImport<
    typeof lazyIndex.warmupCopilotSearchSydney,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.warmupCopilotSearchSydney);

// Export orchestrators
registerLazyOrchestrator(endSearchSession, lazyModule, m => m.endSearchSessionOrchestrator);
registerLazyOrchestrator(onAppBarTrigger, lazyModule, m => m.onAppBarTriggerOrchestrator);
